import React, { useState, useEffect } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import {
  Typography,
  IconButton,
  Box,
  Chip,
  Collapse,
  Divider
} from '@mui/material'
import {
  ArrowBack,
  ExpandMore,
  ExpandLess,
  Close
} from '@mui/icons-material'

// Components Imports
import MailCard from './MailCard'

const MailDetails = ({ inline, activeEmailArr, setRepliedMessage, onClose }) => {
  const { t } = useTranslation()
  const [showEarlier, setShowEarlier] = useState(false)

  const activeMessageId = activeEmailArr?.[0]?.messageId

  useEffect(() => {
    setShowEarlier(false)
  }, [activeMessageId])

  if (!activeEmailArr) return null

  const subject = activeEmailArr?.[0]?.subject || t('mailTemplate.mailbox.noSubject')
  const totalMessages = activeEmailArr?.length || 0

  const earlierMessages = totalMessages > 1 ? activeEmailArr.slice(1) : []
  const latestMessage = activeEmailArr?.[0]

  const content = (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      bgcolor: 'background.paper',
      ...(inline
        ? {}
        : {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200
          }
      )
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        bgcolor: 'background.paper'
      }}>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ flexShrink: 0 }}
        >
          {inline ? <Close sx={{ fontSize: 20 }} /> : <ArrowBack sx={{ fontSize: 20 }} />}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: 1.4,
            color: 'text.primary',
            wordBreak: 'break-word'
          }}>
            {subject}
          </Typography>
          {totalMessages > 1 && (
            <Chip
              size="small"
              label={t('mailTemplate.mailbox.messageCount', '{{count}} messages', { count: totalMessages })}
              sx={{
                height: 20,
                mt: 0.5,
                fontSize: '0.65rem',
                fontWeight: 500,
                bgcolor: 'action.hover',
                color: 'text.secondary'
              }}
            />
          )}
        </Box>
      </Box>

      {/* Message Thread */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        bgcolor: 'background.paper',
        p: 4
      }}>
        {/* Earlier messages toggle */}
        {earlierMessages.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box
              onClick={() => setShowEarlier(!showEarlier)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 1,
                cursor: 'pointer',
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                border: '1px dashed',
                borderColor: 'divider',
                transition: 'all 0.15s',
                '&:hover': { borderColor: 'primary.light', bgcolor: 'primary.50' }
              }}
            >
              {showEarlier
                ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} />
                : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
              }
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: 'text.secondary' }}>
                {showEarlier
                  ? t('mailTemplate.mailbox.hideEarlier', 'Hide earlier messages')
                  : t('mailTemplate.mailbox.earlierMessages', { count: earlierMessages.length })
                }
              </Typography>
            </Box>

            <Collapse in={showEarlier}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
                {earlierMessages.map((msg, index) => (
                  <MailCard
                    key={msg.messageId || `earlier-${index}-${msg.sentTimeMillis}`}
                    data={msg}
                    isReplies={false}
                    setRepliedMessage={setRepliedMessage}
                    compact
                  />
                ))}
                <Divider sx={{ my: 0.5 }}>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 500 }}>
                    {t('mailTemplate.mailbox.latestMessage', 'Latest')}
                  </Typography>
                </Divider>
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Latest message */}
        <MailCard
          data={latestMessage}
          isReplies={true}
          setRepliedMessage={setRepliedMessage}
        />
      </Box>
    </Box>
  )

  return content
}

export default MailDetails
